import { addMonths, addDays } from 'date-fns';
import { useState, useCallback, useMemo } from 'react';

// ============================================================================
// CONSTANTS
// ============================================================================

// Type as CalendarScope
/** @type {CalendarScope[]} */
export const SCOPE_OPTIONS = ['day', 'week', 'month'];

export const DEFAULT_SCOPE = SCOPE_OPTIONS[0]; // 'day' (changed from 'week' for Timeline default view)

export const DEFAULT_SLOT_DURATION = '00:15:00';
export const DEFAULT_START_TIME = '12:00'; // Changed from '08:00' for Timeline default
export const DEFAULT_END_TIME = '02:00'; // Changed from '18:00' for Timeline default (spans midnight)
export const DEFAULT_START_DATE = new Date().toISOString().split('T')[0]; // Today's date in YYYY-MM-DD
export const DEFAULT_END_DATE = new Date().toISOString().split('T')[0]; // Today's date in YYYY-MM-DD

// ============================================================================
// TIME NORMALIZATION - Time Range Calculations
// ============================================================================

/**
 * Normalizes slotMaxTime to handle time ranges that span across midnight.
 * If endTime is earlier than startTime, it's interpreted as the next day.
 * @param {string} startTime - Start time in "HH:mm" format
 * @param {string} endTime - End time in "HH:mm" format
 * @returns {string} Normalized end time in "HH:00:00" format (can be >24h if spanning midnight)
 * @example
 * normalizeSlotMaxTime("18:00", "02:00") // Returns "26:00:00" (2h next day = 24 + 2)
 * normalizeSlotMaxTime("08:00", "18:00") // Returns "18:00:00" (same day)
 */
const normalizeSlotMaxTime = (startTime, endTime) => {
  // Parse times to extract hours and minutes
  const parseTime = (/** @type {string} */ timeStr) => {
    const [hours, minutes = '00'] = timeStr.split(':');
    return {
      hours: parseInt(hours, 10),
      minutes: parseInt(minutes, 10),
      totalMinutes: parseInt(hours, 10) * 60 + parseInt(minutes, 10),
    };
  };

  const start = parseTime(startTime);
  const end = parseTime(endTime);

  // If endTime is earlier than startTime, it means it's the next day
  // Convert to >24h format for FullCalendar
  if (end.totalMinutes < start.totalMinutes) {
    // Add 24 hours to the end time
    const nextDayHours = end.hours + 24;
    return `${nextDayHours.toString().padStart(2, '0')}:00:00`;
  }

  // Same day: return endTime in "HH:00:00" format
  return `${end.hours.toString().padStart(2, '0')}:00:00`;
};

/**
 * Normalizes slotMinTime to ensure it's in "HH:00:00" format.
 * @param {string} time - Time in "HH:mm" or "HH:mm:ss" format
 * @returns {string} Normalized time in "HH:00:00" format
 * @example
 * normalizeSlotMinTime("18:00") // Returns "18:00:00"
 * normalizeSlotMinTime("08:30") // Returns "08:00:00"
 */
const normalizeSlotMinTime = (time) => {
  const [hours] = time.split(':');
  const hoursInt = parseInt(hours, 10);
  return `${hoursInt.toString().padStart(2, '0')}:00:00`;
};

// ============================================================================
// DATE MANIPULATION - Date Calculations
// ============================================================================
/**
 *
 * @param {Date} date
 * @param {CalendarScope} scope
 * @param {"minus" | "plus"} direction
 * @returns {Date}
 */
export const changeDate = (date, scope, direction) => {
  if (direction === 'plus') {
    switch (scope) {
      case 'day':
        return addDays(date, 1);
      case 'week':
        return addDays(date, 7);
      case 'month':
        return addMonths(date, 1);
      default:
        return addDays(date, 7);
    }
  } else {
    // means direction === 'minus'
    switch (scope) {
      case 'day':
        return addDays(date, -1);
      case 'week':
        return addDays(date, -7);
      case 'month':
        return addMonths(date, -1);
      default:
        return addDays(date, -7);
    }
  }
};

// ============================================================================
// MAIN HOOK
// ============================================================================

/**
 * Custom hook to manage FullCalendar state and behavior
 * @param {*} data
 * @param {Array<{field: string, group?: boolean, headerContent?: string}>} [resourceAreaColumns] - Resource area columns configuration
 * @returns {{
 *   startTime: string,
 *   endTime: string,
 *   normalizedSlotMinTime: string,
 *   normalizedSlotMaxTime: string,
 *   slotDuration: string,
 *   setSlotDuration: (value: string) => void,
 *   setStartTime: (value: string) => void,
 *   setEndTime: (value: string) => void,
 *   scope: CalendarScope,
 *   setScope: (value: CalendarScope) => void,
 *   toggleResourceVisibility: (resourceId: string) => void,
 *   toggleGroupVisibility: (groupValue: string, groupField: string) => void,
 *   isResourceVisible: (resourceId: string) => boolean,
 *   visibleResources: Array<CalendarResource>,
 *   groupedResources: Record<string, Array<CalendarResource>>,
 *   resourcesGroups: Array<{lib: string, options: Array<CalendarResource>}>,
 *   resourceGroupField: string | null,
 *   getGroupVisibilityState: (groupValue: string) => 'all-visible' | 'all-hidden' | 'mixed' | null,
 *   showResourcesModal: boolean,
 *   openResourcesModal: () => void,
 *   closeResourcesModal: () => void,
 *   searchResources: string,
 *   setSearchResources: (search: string) => void,
 * }}
 */
const useCalendarHeader = (data, resourceAreaColumns = []) => {
  // ============================================================================
  // STATE
  // ============================================================================

  // Calendar settings
  const [startTime, setStartTime] = useState(DEFAULT_START_TIME);
  const [endTime, setEndTime] = useState(DEFAULT_END_TIME);
  const [slotDuration, setSlotDuration] = useState(DEFAULT_SLOT_DURATION);
  const [scope, setScope] = useState(DEFAULT_SCOPE);

  // Resources
  const [hiddenResources, setHiddenResources] = useState(
    /** @type {Set<string>} */ (new Set()),
  );

  // Resources management modal
  const [showResourcesModal, setShowResourcesModal] = useState(false);
  const [searchResources, setSearchResources] = useState('');

  // ============================================================================
  // TIME NORMALIZATION
  // ============================================================================

  // Normalize time values for FullCalendar (handles midnight-spanning ranges)
  const normalizedSlotMinTime = useMemo(
    () => {
      if (startTime === '') {
        // Check used to prevent crash if the user tries to delete the time instead of giving a new one.
        return normalizeSlotMinTime(DEFAULT_START_TIME);
      }
      return normalizeSlotMinTime(startTime);
    },
    [startTime],
  );

  const normalizedSlotMaxTime = useMemo(
    () => {
      if (endTime === '') {
        // Check used to prevent crash if the user tries to delete the time instead of giving a new one.
        return normalizeSlotMaxTime(DEFAULT_END_TIME, DEFAULT_START_TIME);
      }
      return normalizeSlotMaxTime(startTime, endTime);
    },
    [startTime, endTime],
  );

  // ============================================================================
  // RESOURCE MANAGEMENT
  // ============================================================================

  const toggleResourceVisibility = useCallback(
    (/** @type {string} */ resourceId) => {
      setHiddenResources((/** @type {Set<string>} */ prev) => {
        const newSet = new Set(prev);
        if (newSet.has(resourceId)) {
          newSet.delete(resourceId);
        } else {
          newSet.add(resourceId);
        }
        return newSet;
      });
    },
    [],
  );

  const openResourcesModal = useCallback(() => {
    setShowResourcesModal(true);
  }, []);

  const closeResourcesModal = useCallback(() => {
    setShowResourcesModal(false);
  }, []);

  const visibleResources = useMemo(
    () => (data?.resources || []).filter(
      (/** @type {CalendarResource} */ resource) => !hiddenResources.has(resource.id),
    ),
    [data?.resources, hiddenResources],
  );

  const isResourceVisible = useCallback(
    (/** @type {string} */ resourceId) => !hiddenResources.has(resourceId),
    [hiddenResources],
  );

  // Find the group field from resourceAreaColumns
  const groupColumn = useMemo(() => resourceAreaColumns.find((col) => col.group === true), [resourceAreaColumns]);

  const resourceGroupField = groupColumn?.field || null;

  // Group resources by the group field
  /** @type {Record<string, Array<CalendarResource>>} */
  const groupedResources = useMemo(() => {
    if (!resourceGroupField) {
      // If no group field, return all resources in a single group
      return { '': data?.resources || [] };
    }

    /** @type {Record<string, Array<CalendarResource>>} */
    const groups = {};
    (data?.resources || []).forEach(
      (/** @type {CalendarResource} */ resource) => {
        const groupValue = (/** @type {string} */ (resource[resourceGroupField])) || '';
        if (!groups[groupValue]) {
          groups[groupValue] = [];
        }
        groups[groupValue].push(resource);
      },
    );
    return groups;
  }, [data?.resources, resourceGroupField]);

  // Transform grouped resources to the format expected by ColumnsVisibility component
  // Format: Array<{lib: string, options: Array<CalendarResource>}>
  const resourcesGroups = useMemo(() => Object.entries(groupedResources).map(
    ([groupValue, groupResources]) => ({
      lib: groupValue || 'Sans groupe',
      options: groupResources,
    }),
  ), [groupedResources]);

  /**
   * Get group visibility state
   * @param {string} groupValue - The value of the group
   * @returns {'all-visible' | 'all-hidden' | 'mixed' | null}
   */
  const getGroupVisibilityState = useCallback(
    (/** @type {string} */ groupValue) => {
      if (!resourceGroupField) return null;
      const groupResources = groupedResources[groupValue] || [];
      if (groupResources.length === 0) return null;

      const visibleCount = groupResources.filter(
        (/** @type {CalendarResource} */ r) => isResourceVisible(r.id),
      ).length;
      if (visibleCount === 0) return 'all-hidden';
      if (visibleCount === groupResources.length) return 'all-visible';
      return 'mixed';
    },
    [resourceGroupField, groupedResources, isResourceVisible],
  );

  /**
   * Toggle visibility of all resources in a group
   * @param {string} groupValue - The value of the group (e.g., "Bâtiment 1")
   * @param {string} groupField - The field name used for grouping (e.g., "building")
   */
  const toggleGroupVisibility = useCallback(
    (/** @type {string} */ groupValue, /** @type {string} */ groupField) => {
      // Find all resources in the group
      const groupResources = (data?.resources || []).filter(
        (/** @type {CalendarResource} */ resource) => {
          const fieldValue = (/** @type {string} */ (resource[groupField]));
          return fieldValue === groupValue;
        },
      );

      if (groupResources.length === 0) return;

      // Check if all resources in the group are visible
      const allVisible = groupResources.every(
        (/** @type {CalendarResource} */ resource) => !hiddenResources.has(resource.id),
      );

      // Toggle all resources in the group
      setHiddenResources((/** @type {Set<string>} */ prev) => {
        const newSet = new Set(prev);
        groupResources.forEach((/** @type {CalendarResource} */ resource) => {
          if (allVisible) {
            newSet.add(resource.id); // Hide all
          } else {
            newSet.delete(resource.id); // Show all
          }
        });
        return newSet;
      });
    },
    [data?.resources, hiddenResources],
  );

  // ============================================================================
  // RETURN
  // ============================================================================

  return {
    // Calendar settings
    startTime,
    endTime,
    normalizedSlotMinTime,
    normalizedSlotMaxTime,
    slotDuration,
    scope,
    setSlotDuration,
    setStartTime,
    setEndTime,
    setScope,

    // Resources
    visibleResources,
    isResourceVisible,
    toggleResourceVisibility,
    toggleGroupVisibility,
    groupedResources,
    resourcesGroups,
    resourceGroupField,
    getGroupVisibilityState,

    // Resources management modal
    showResourcesModal,
    openResourcesModal,
    closeResourcesModal,
    searchResources,
    setSearchResources,
  };
};

export default useCalendarHeader;
